import createClient from "openapi-fetch";
import type { Client } from "openapi-fetch";
import type { paths as LegacyPaths } from "./generated/legacy";
import type { paths as PriceworkPaths } from "./generated/pricework";
import type { paths as PortalGatewayPaths } from "./generated/portalGateway";
import type { paths as MobileGatewayPaths } from "./generated/mobileGateway";
import {
  createAuthMiddleware,
  type CreateBilldaClientOptions,
} from "./middleware";

export type { CreateBilldaClientOptions } from "./middleware";

/** Default per-request timeout, matching the axios client's 15s. */
export const DEFAULT_TIMEOUT_MS = 15000;

/** Typed clients for every public API surface, sharing one auth middleware instance. */
export interface BilldaClient {
  legacy: Client<LegacyPaths>;
  pricework: Client<PriceworkPaths>;
  portalGateway: Client<PortalGatewayPaths>;
  mobileGateway: Client<MobileGatewayPaths>;
}

/**
 * Combine the request-timeout signal with any caller-provided signal. Prefers
 * the native AbortSignal.any; falls back to a manual controller for older
 * runtimes.
 */
function combineSignals(signals: AbortSignal[]): AbortSignal {
  const anyFn = (AbortSignal as { any?: (s: AbortSignal[]) => AbortSignal })
    .any;
  if (typeof anyFn === "function") {
    return anyFn(signals);
  }
  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    signal.addEventListener("abort", () => controller.abort(signal.reason), {
      once: true,
    });
  }
  return controller.signal;
}

/**
 * Wrap fetch so every request aborts after `timeoutMs`. Races the combined
 * abort signal against the fetch so the promise rejects even if the underlying
 * fetch implementation ignores the signal. A caller-provided signal still
 * aborts (sooner, if it fires first).
 */
function createTimeoutFetch(timeoutMs: number): typeof fetch {
  return (input, init) => {
    const signals: AbortSignal[] = [AbortSignal.timeout(timeoutMs)];
    // openapi-fetch calls fetch(request) with the caller signal on the Request
    // object, not in init, so read both.
    const callerSignal =
      init?.signal ?? (input instanceof Request ? input.signal : null);
    if (callerSignal) {
      signals.push(callerSignal);
    }
    const signal = combineSignals(signals);
    const fetchPromise = fetch(input, { ...init, signal });

    return new Promise<Response>((resolve, reject) => {
      const onAbort = (): void => reject(signal.reason);
      fetchPromise.then(
        (response) => {
          signal.removeEventListener("abort", onAbort);
          resolve(response);
        },
        (error) => {
          signal.removeEventListener("abort", onAbort);
          reject(error);
        },
      );

      if (signal.aborted) {
        reject(signal.reason);
        return;
      }
      signal.addEventListener("abort", onAbort, { once: true });
    });
  };
}

/**
 * Create the typed Billda API clients. They share one auth middleware so a
 * refresh triggered on one is deduped across every public API surface.
 */
export function createBilldaClient(
  options: CreateBilldaClientOptions,
): BilldaClient {
  const middleware = createAuthMiddleware(options);
  const fetch = createTimeoutFetch(options.timeoutMs ?? DEFAULT_TIMEOUT_MS);

  const legacy = createClient<LegacyPaths>({ baseUrl: options.baseUrl, fetch });
  const pricework = createClient<PriceworkPaths>({
    baseUrl: options.baseUrl,
    fetch,
  });
  const portalGateway = createClient<PortalGatewayPaths>({
    baseUrl: options.portalGatewayBaseUrl ?? options.baseUrl,
    fetch,
  });
  const mobileGateway = createClient<MobileGatewayPaths>({
    baseUrl: options.mobileGatewayBaseUrl ?? options.baseUrl,
    fetch,
  });

  legacy.use(middleware);
  pricework.use(middleware);
  portalGateway.use(middleware);
  mobileGateway.use(middleware);

  return { legacy, pricework, portalGateway, mobileGateway };
}
